import { Cosmetics, CosmeticsState, cosmeticsSection } from "@highmarch/app";
import type { BalanceData } from "@highmarch/domain";
import { WornSet, WornSkins } from "../render/skins/wornSkins";
import { ReadabilitySheet } from "../render/skins/readabilitySheet";
import { SkinThumbs } from "../render/skins/skinThumb";
import { BaseSkin } from "../render/views/baseSkin";
import { closeBtnHtml } from "../ui/kit";
import { icon } from "../ui/iconsKit";
import { toast } from "../ui/toast";
import { economyChanged, economyLink } from "../ui/hub/economyLink";
import { openMarket } from "../ui/hub/marketSheet";
import { setSkinsLink } from "../ui/hub/skinsLink";
import { openSkins } from "../ui/hub/skinsSheet";
import { SkinSwitcher } from "../ui/debug/skinSwitcher";
import { petLink } from "../ui/pets/petLink";
import { Feature, MORE_ORDER } from "./registry";
import { ECONOMY_FROM_CLEARED } from "./economy";
import "../ui/styles/skins.css";

/** More-sheet slot for Skins: after the Market (20), Achievements (30) and the Almanac (40). */
const SKINS_ORDER = 45;
/** Settings slot for the skin switcher: after Reminders (40), before Copy diagnostics (80). */
const SKIN_SWITCH_ORDER = 70;

/** What the renderer should wear, from the rules' answer (a skin no longer had reads as stock). */
export function wornSet(k: Cosmetics, balance: BalanceData): WornSet {
  const towers = new Map<string, string>();
  for (const t of balance.towers) if (!t.isBase) { const id = k.worn("tower", t.id); if (id) towers.set(t.id, id); }
  const pets = new Map<string, string>();
  for (const p of balance.pets.list) { const id = k.worn("pet", p.id); if (id) pets.set(p.id, id); }
  return { towers, fortress: k.worn("fortress"), troop: k.worn("troop"), emblem: k.worn("emblem"), road: k.worn("road"), frame: k.worn("frame"), kill: k.worn("kill"), pets, pet: petLink.equipped(), petsOwned: petLink.owned().map((p) => p.id) };
}

/**
 * Skins and cosmetics (FINISH_PLAN WP8): the `cosmetics` save section (bound, so every save writes it; a save from
 * before it takes over the old `td.baseSkin` toggle once), the worn skins pushed to the renderer at boot and on every
 * change, the picker's link (Towers tab, Shop shelf, Black Market card), a More row, a red count on the Towers tab
 * for skins unlocked and not yet seen, and the debug readability sheet. The rules are @highmarch/app `Cosmetics`; the looks
 * are render/skins/. The Guide's skins topic is WP9's.
 */
export const cosmeticsFeature: Feature = {
  id: "cosmetics",
  register(ctx, registry) {
    const legacy = BaseSkin.legacy();
    const { state, migrated } = CosmeticsState.fromSave(ctx.balance, ctx.save, legacy);
    // a pet's skin (docs/PETS.md) is had once a wild pup wearing it is caught (Pets v2)
    const k = new Cosmetics(ctx.catalog, state);
    if (!cosmeticsSection.read(ctx.save)) k.markAllSeen();          // a first run of skins: what is had already is not news
    cosmeticsSection.bind(ctx.save, () => state.toJson());
    // Skins a Deals offer pays (docs/SHOP.md §1.3 Master's Collection): unlocked as owned; one already had pays its refund.
    ctx.catalog.attachSkins({ owns: (id) => state.owned.has(id) || k.has(id), unlock: (id) => { state.owned.add(id); } });
    // the debug skin switcher (?debug / ?skins) lays its override over the save's set; release builds wear the save's
    SkinSwitcher.catalogue = k.catalogue;
    SkinSwitcher.towers = ctx.balance.towers.filter((t) => !t.isBase).map((t) => t.id);
    SkinSwitcher.fromUrl();
    const wear = () => WornSkins.set(SkinSwitcher.apply(wornSet(k, ctx.balance)));
    SkinSwitcher.onChange = wear;
    petLink.onChange(wear);                 // the pet equipped (or caught) is worn at once
    wear();
    SkinSwitcher.mountFab(ctx.ui);
    if (migrated || !cosmeticsSection.read(ctx.save)) ctx.persist();
    if (legacy) BaseSkin.clearLegacy();
    const changed = () => { wear(); economyChanged(); };
    setSkinsLink({
      cosmetics: k, now: ctx.now, changed,
      towers: () => ctx.balance.towers.filter((t, i) => !t.isBase && ctx.catalog.ownedTowers()[i]).map((t) => ({ id: t.id, name: t.name, level: ctx.catalog.profile.towerLevels[t.index] })),
      baseLevel: () => ctx.catalog.profile.baseLevel,
      openBlackMarket: () => { const hub = economyLink()?.hub; if (hub) openMarket({ hub, catalog: ctx.catalog, now: ctx.now }, "black"); },
    });
    registry.moreRow({
      id: "skins", order: SKINS_ORDER, icon: "skins", home: "shop",   // M35: Towers → Skins and the Shop's Gems page
      label: () => "Skins",
      note: () => `${k.haveCount()} of ${k.catalogue.length} · looks only`,
      dot: () => k.unseen().length,
      visible: () => ctx.catalog.highestLevelCleared >= ECONOMY_FROM_CLEARED,      // with the Shop and the Market
      open: (hub) => { openSkins(hub.parent); },
    });
    registry.tabBadge({ tab: "towers", count: () => k.unseen().length });
    // Settings → Skin switcher (testing; on every build for now): any skin on the hub or mid-battle, the save untouched
    registry.settingsRow({
      id: "skinSwitch", order: SKIN_SWITCH_ORDER, scope: "everywhere",
      title: () => "Skin switcher", sub: () => SkinSwitcher.summary(),
      control: () => `<button class="btn dark ssw-open"><span>Open</span>${icon("chev", 16)}</button>`,
      bind: (row, api) => { row.querySelector<HTMLButtonElement>(".ssw-open")!.onclick = () => { api.close(); SkinSwitcher.open(api.parent); }; },
    });
    if (ctx.debug) {
      BaseSkin.debugSet = (model) => {
        const id = model ? `fortress.${model}` : null;
        if (id) state.owned.add(id);
        k.equip("fortress", id);
        changed();
      };
      registry.moreRow({
        id: "skinSwitch", order: MORE_ORDER.debug + 1, icon: "skins",
        label: () => "Debug: skin switcher", visible: () => ctx.debug,
        open: (hub) => { SkinSwitcher.open(hub.parent); },
      });
      registry.moreRow({
        id: "skinSheet", order: MORE_ORDER.debug + 2, icon: "flag",
        label: () => "Debug: skin readability sheet", visible: () => ctx.debug,
        open: (hub) => { void showReadability(hub.parent, ctx.balance.towers.filter((t) => !t.isBase).map((t) => t.id)); },
      });
      (window as unknown as { __tdSkins: unknown }).__tdSkins = {
        cosmetics: k, wear: changed, switcher: SkinSwitcher, open: (o?: Parameters<typeof openSkins>[1]) => openSkins(ctx.ui, o),
        /** Owns every skin (debug captures). */
        ownAll: () => { for (const s of k.catalogue) state.owned.add(s.id); changed(); },
        sheet: () => showReadability(ctx.ui, ctx.balance.towers.filter((t) => !t.isBase).map((t) => t.id)),
        /** A skin's thumbnail as a PNG data URL (tools/art/skins/thumbs.mjs writes art/interface/skins/ from it). */
        thumb: async (kind: Parameters<typeof SkinThumbs.render>[0]["kind"], skin: string | null, px: number, tier?: number, tower?: string) => (await SkinThumbs.render({ kind, skin, tier, tower }, px)).toDataURL("image/png"),
        /** A tower kit's capture sheet (every tower type, or `towers`, × 3 tiers, stock beside it) as a PNG data URL. */
        kitSheet: async (skin: string, cell?: number, markers?: boolean, towers?: string[]) => (await SkinThumbs.kitSheet(skin, cell, markers, towers)).toDataURL("image/png"),
      };
    }
  },
};

async function showReadability(parent: HTMLElement, towers: readonly string[]): Promise<void> {
  toast(parent, "Rendering every skin…");
  const cv = await ReadabilitySheet.render(towers);
  const sheet = document.createElement("div");
  sheet.className = "sheet panel skinsheet more";
  sheet.innerHTML = `<div class="sheethead"><div class="titles"><b class="disp">Skin sheet</b><small>Every skin at game distance</small></div>${closeBtnHtml()}</div>`;
  cv.style.width = "100%";
  cv.style.imageRendering = "pixelated";
  sheet.append(cv);
  sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
  parent.append(sheet);
}
